/**
 * Periodic actuals — SAP backend implementation.
 *
 * Part of the isolated `sap-backend/` side project (see ../README.md). This is
 * the first entity wired end-to-end against S/4 as a proof of the flip: it
 * maps the dataService `getPeriodicActuals(projectId)` contract onto live
 * ACDOCA postings read through the already-working ZUI_M12_WBS_ACTUALS_O4
 * OData service.
 *
 * Source of truth: SAP universal journal (ACDOCA), aggregated by project / WBS
 * / fiscal period / cost element. We delegate the HTTP + proxy handling to the
 * existing wbsActualsService (also a SAP service, no Supabase), then reshape
 * its rows into the app's EvmPeriodicActual contract.
 */

import type { EvmPeriodicActual } from '@/types/evm'
import { listActualsForProject, fiscalRowToPeriod } from '@/services/wbsActualsService'

/** Fiscal years to sweep when no explicit year is given. Current + prior two
 *  covers the active project window without an expensive full-history scan. */
function recentFiscalYears(): string[] {
  const now = new Date().getFullYear()
  return [now, now - 1, now - 2].map(String)
}

/**
 * Read posted actuals for a project from ACDOCA and reshape to
 * EvmPeriodicActual[] (one row per WBS × period). Without a projectId the
 * universal journal cannot be cheaply scoped, so we return [] and warn —
 * EVM screens always call this per project.
 */
export async function getPeriodicActuals(projectId?: string): Promise<EvmPeriodicActual[]> {
  if (!projectId) {
    console.warn('[sap-backend] getPeriodicActuals requires a projectId in sap mode; returning []')
    return []
  }

  const years = recentFiscalYears()
  const perYear = await Promise.all(
    years.map(async (fy) => {
      try {
        return await listActualsForProject(projectId, fy)
      } catch (err) {
        // A year with no postings can 404/empty; don't fail the whole read.
        console.warn(`[sap-backend] actuals read for ${projectId} FY${fy} failed:`, err)
        return []
      }
    }),
  )

  // Aggregate to WBS × period (the ZC_M12_WBS_ACTUALS view already groups by
  // cost element, so we sum cost elements up to the period grain the app uses).
  const acc = new Map<string, EvmPeriodicActual>()
  for (const row of perYear.flat()) {
    const period = fiscalRowToPeriod(row)
    const key = `${row.WbsElement}|${period}`
    const existing = acc.get(key)
    if (existing) {
      existing.ac += row.ActualCost ?? 0
      existing.hoursWorked += row.ActualQuantity ?? 0
    } else {
      acc.set(key, {
        projectId,
        wbsId: row.WbsElement,
        period,
        ac: row.ActualCost ?? 0,
        hoursWorked: row.ActualQuantity ?? 0,
      })
    }
  }
  return [...acc.values()]
}
